import styleIcon from 'uiComponents/studio/icons/style.svg'
import copyIcon from 'uiComponents/studio/icons/copy.svg'
import restrictionsIcon from 'uiComponents/studio/icons/target.svg'
import settingsIcon from 'uiComponents/studio/icons/settings.svg'
import { SectionObject } from 'uiComponents/studio/menuItem'
import { t } from 'translations'
import { fontOptions } from '../shared'

export interface FormDefinitionSchema {
    text: SectionObject
    style: SectionObject
    targeting: SectionObject
    settings: SectionObject
}

export const getModalWindowFormDefinition = (): FormDefinitionSchema => ({
    text: {
        name: 'text',
        label: t('engageTools.studio.config.steps.text', { step: 2 }),
        header: t('engageTools.studio.config.headers.createMessage'),
        icon: copyIcon,
        description: t('engageTools.studio.modalWindow.descriptions.text'),
        sections: [
            {
                name: 'Languages',
                label: t('engageTools.studio.config.sections.languages'),
                fields: [],
            },
        ],
    },
    style: {
        name: 'style',
        label: t('engageTools.studio.config.steps.style', { step: 1 }),
        header: t('engageTools.studio.config.headers.designLookAndFeel'),
        icon: styleIcon,
        description: t('engageTools.studio.modalWindow.descriptions.style'),
        sections: [
            {
                name: t('engageTools.studio.config.sections.general'),
                fields: [
                    {
                        name: 'header-logo',
                        type: 'upload',
                        label: t('engageTools.studio.config.fields.logo'),
                        span: 12,
                        path: 'styling.modal.headerLogo',
                        defaultValue: 'true',
                    },
                    {
                        name: 'corners',
                        type: 'select',
                        availableOptions: [
                            {
                                name: t('engageTools.studio.config.options.rounded'),
                                value: 'rounded',
                            },
                            {
                                name: t('engageTools.studio.config.options.square'),
                                value: 'square',
                            },
                        ],
                        label: t('engageTools.studio.config.fields.corners'),
                        span: 8,
                        path: 'styling.modal.cornersRounded',
                        defaultValue: 'square',
                    },
                    {
                        name: 'font-family',
                        type: 'select',
                        availableOptions: [...fontOptions],
                        label: t('engageTools.studio.config.fields.fontFamily'),
                        span: 8,
                        path: 'styling.fontFamily',
                        defaultValue: 'Roboto',
                    },
                    {
                        name: 'header-color',
                        type: 'picker',
                        label: t('engageTools.studio.config.fields.headerColor'),
                        span: 6,
                        path: 'styling.modal.headerBGColor',
                        defaultValue: '',
                    },
                    {
                        name: 'overlay-color',
                        type: 'picker',
                        label: t('engageTools.studio.config.fields.overlayColor'),
                        span: 6,
                        path: 'styling.modal.overlayColor',
                        defaultValue: '#000000',
                    },
                ],
            },
            {
                name: t('engageTools.studio.config.sections.title'),
                fields: [
                    {
                        name: 'text-size',
                        type: 'select',
                        availableOptions: [
                            {
                                name: t('engageTools.studio.config.options.small'),
                                value: 'small',
                            },
                            {
                                name: t('engageTools.studio.config.options.medium'),
                                value: 'medium',
                            },
                            {
                                name: t('engageTools.studio.config.options.large'),
                                value: 'large',
                            },
                        ],
                        label: t('engageTools.studio.config.fields.textSize'),
                        span: 8,
                        path: 'styling.title.size',
                        defaultValue: 'medium',
                    },
                    {
                        name: 'text-color',
                        type: 'picker',
                        label: t('engageTools.studio.config.fields.textColor'),
                        span: 6,
                        path: 'styling.title.fontColor',
                        defaultValue: '#999999',
                    },
                ],
            },
            {
                name: t('engageTools.studio.config.sections.paragraph'),
                fields: [
                    {
                        name: 'text-size',
                        type: 'select',
                        availableOptions: [
                            {
                                name: t('engageTools.studio.config.options.small'),
                                value: 'small',
                            },
                            {
                                name: t('engageTools.studio.config.options.medium'),
                                value: 'medium',
                            },
                            {
                                name: t('engageTools.studio.config.options.large'),
                                value: 'large',
                            },
                        ],
                        label: t('engageTools.studio.config.fields.textSize'),
                        span: 8,
                        path: 'styling.paragraph.size',
                        defaultValue: 'medium',
                    },
                    {
                        name: 'text-color',
                        type: 'picker',
                        label: t('engageTools.studio.config.fields.textColor'),
                        span: 6,
                        path: 'styling.paragraph.fontColor',
                        defaultValue: '#999999',
                    },
                ],
            },
            {
                name: t('engageTools.studio.config.sections.icon'),
                fields: [
                    {
                        name: 'show-icon',
                        type: 'toggle',
                        label: t('engageTools.studio.config.fields.showIcon'),
                        path: 'hideIconButton',
                        defaultValue: false,
                        infotip: t('engageTools.studio.config.infotips.showIcon'),
                        span: 6,
                    },
                    {
                        name: 'icon-url',
                        type: 'upload',
                        label: '',
                        span: 12,
                        path: 'styling.iconButton.iconSource',
                        defaultValue: '',
                    },
                    {
                        name: 'background-color',
                        type: 'picker',
                        label: t('engageTools.studio.config.fields.backgroundColor'),
                        span: 6,
                        path: 'styling.iconButton.bgColor',
                        defaultValue: '#DDDDDD',
                    },
                ],
            },
            {
                name: t('engageTools.studio.config.sections.mainButton'),
                fields: [
                    {
                        name: 'show-button',
                        type: 'toggle',
                        label: t('engageTools.studio.config.fields.showProceedButton'),
                        path: 'hideProceedButton',
                        defaultValue: false,
                        infotip: t('engageTools.studio.config.infotips.showProceedButton'),
                        span: 6,
                    },
                    {
                        name: 'button-size',
                        type: 'select',
                        availableOptions: [
                            {
                                name: t('engageTools.studio.config.options.small'),
                                value: 'small',
                            },
                            {
                                name: t('engageTools.studio.config.options.medium'),
                                value: 'medium',
                            },
                            {
                                name: t('engageTools.studio.config.options.large'),
                                value: 'large',
                            },
                        ],
                        label: t('engageTools.studio.config.fields.buttonSize'),
                        span: 8,
                        path: 'styling.proceedButton.size',
                        defaultValue: 'medium',
                    },
                    {
                        name: 'background-color',
                        type: 'picker',
                        label: t('engageTools.studio.config.fields.backgroundColor'),
                        span: 6,
                        path: 'styling.proceedButton.bgColor',
                        defaultValue: '#FFFF00',
                    },
                    {
                        name: 'text-color',
                        type: 'picker',
                        label: t('engageTools.studio.config.fields.textColor'),
                        span: 6,
                        path: 'styling.proceedButton.fontColor',
                        defaultValue: '#999999',
                    },
                ],
            },
            {
                name: t('engageTools.studio.config.sections.dismissText'),
                fields: [
                    {
                        name: 'dismiss-text',
                        type: 'select',
                        availableOptions: [
                            {
                                name: t('engageTools.studio.config.options.small'),
                                value: 'small',
                            },
                            {
                                name: t('engageTools.studio.config.options.medium'),
                                value: 'medium',
                            },
                            {
                                name: t('engageTools.studio.config.options.large'),
                                value: 'large',
                            },
                        ],
                        label: t('engageTools.studio.config.fields.textSize'),
                        span: 8,
                        path: 'styling.dismissButton.size',
                        defaultValue: 'medium',
                    },
                    {
                        name: 'dismiss-text-color',
                        type: 'picker',
                        label: t('engageTools.studio.config.fields.textColor'),
                        span: 6,
                        path: 'styling.dismissButton.fontColor',
                        defaultValue: '#999999',
                    },
                ],
            },
            {
                name: t('engageTools.studio.config.sections.dismissIcon'),
                fields: [
                    {
                        name: 'icon-size',
                        type: 'select',
                        availableOptions: [
                            {
                                name: t('engageTools.studio.config.options.small'),
                                value: 'small',
                            },
                            {
                                name: t('engageTools.studio.config.options.medium'),
                                value: 'medium',
                            },
                            {
                                name: t('engageTools.studio.config.options.large'),
                                value: 'large',
                            },
                        ],
                        label: t('engageTools.studio.config.fields.iconSize'),
                        span: 8,
                        path: 'styling.xButton.size',
                        defaultValue: 'small',
                    },
                    {
                        name: 'icon-color',
                        type: 'picker',
                        label: t('engageTools.studio.config.fields.iconColor'),
                        span: 6,
                        path: 'styling.xButton.fontColor',
                        defaultValue: '#FFFFFF',
                    },
                ],
            },
        ],
    },
    targeting: {
        name: 'targeting',
        label: t('engageTools.studio.config.steps.targeting', { step: 4 }),
        header: t('engageTools.studio.config.headers.setRestrictions'),
        icon: restrictionsIcon,
        description: t('engageTools.studio.modalWindow.descriptions.targeting'),
        sections: [
            {
                name: 'Audience',
                fields: [],
            },
            {
                name: t('engageTools.studio.config.sections.displayOn'),
                infotip: t('engageTools.studio.modalWindow.infotips.displayOn'),
                fields: [
                    {
                        name: 'display',
                        type: 'select',
                        availableOptions: [
                            {
                                name: t('engageTools.studio.config.options.allDevices'),
                                value: 'all',
                            },
                            {
                                name: t('engageTools.studio.config.options.onlyMobile'),
                                value: 'mobile',
                            },
                            {
                                name: t('engageTools.studio.config.options.onlyDesktop'),
                                value: 'desktop',
                            },
                        ],
                        label: '',
                        span: 12,
                        path: 'target',
                        defaultValue: 'all',
                    },
                ],
            },
            {
                name: 'Blacklist',
                label: t('engageTools.studio.modalWindow.blacklistLabel'),
                infotip: t('engageTools.studio.modalWindow.infotips.blacklist'),
                fields: [],
            },
        ],
    },
    settings: {
        name: 'settings',
        label: t('engageTools.studio.config.steps.settings', { step: 3 }),
        header: t('engageTools.studio.config.headers.setVisibilityRules'),
        icon: settingsIcon,
        description: t('engageTools.studio.modalWindow.descriptions.settings'),
        sections: [
            {
                name: 'Display rules',
                label: t('engageTools.studio.config.sections.displayRules'),
                fields: [
                    {
                        name: 'display-trigger',
                        type: 'select',
                        availableOptions: [
                            {
                                name: t('engageTools.studio.config.options.afterTimeSpent'),
                                value: 'afterDelay',
                            },
                            {
                                name: t('engageTools.studio.config.options.onSpecificUrl'),
                                value: 'onSpecificUrl',
                            },
                            {
                                name: t('engageTools.studio.config.options.onPageLoad'),
                                value: 'onPageLoad',
                            },
                            {
                                name: t('engageTools.studio.config.options.onExitIntent'),
                                value: 'exitIntent',
                            },
                            {
                                name: t('engageTools.studio.config.options.onScrollDown50'),
                                value: 'scrollDown50',
                            },
                            {
                                name: t('engageTools.studio.config.options.onScrollUp'),
                                value: 'scrollUp',
                            },
                        ],
                        label: t('engageTools.studio.config.fields.display'),
                        span: 8,
                        path: 'trigger',
                        defaultValue: 'afterDelay',
                    },
                    {
                        name: 'show-after',
                        type: 'number',
                        min: 0,
                        max: 120,
                        unit: 'sec',
                        label: t('engageTools.studio.config.fields.showAfter'),
                        span: 6,
                        path: 'displayDelayPeriod',
                        defaultValue: 5,
                    },
                    {
                        name: 'minimum-stay',
                        type: 'number',
                        min: 0,
                        max: 500,
                        unit: 'sec',
                        label: t('engageTools.studio.config.fields.minimumStay'),
                        span: 6,
                        path: 'minStayOnSite',
                        defaultValue: 180,
                        infotip: t('engageTools.studio.modalWindow.infotips.minimumStay'),
                    },
                    {
                        name: 'user-converted-timeout',
                        type: 'select',
                        availableOptions: [
                            {
                                name: t('engageTools.studio.config.options.none'),
                                value: 0,
                            },
                            {
                                name: t('engageTools.studio.config.durations.day'),
                                value: 86400,
                            },
                            {
                                name: t('engageTools.studio.config.durations.days', { days: 2 }),
                                value: 172800,
                            },
                            {
                                name: t('engageTools.studio.config.durations.days', { days: 3 }),
                                value: 259200,
                            },
                            {
                                name: t('engageTools.studio.config.durations.days', { days: 4 }),
                                value: 345600,
                            },
                            {
                                name: t('engageTools.studio.config.durations.days', { days: 5 }),
                                value: 432000,
                            },
                            {
                                name: t('engageTools.studio.config.durations.days', { days: 6 }),
                                value: 518400,
                            },
                            {
                                name: t('engageTools.studio.config.durations.days', { days: 7 }),
                                value: 604800,
                            },
                            {
                                name: t('engageTools.studio.config.durations.days', { days: 14 }),
                                value: 1209600,
                            },
                            {
                                name: t('engageTools.studio.config.durations.days', { days: 30 }),
                                value: 2592000,
                            },
                        ],
                        label: t('engageTools.studio.config.fields.silenceAfterPurchase'),
                        span: 8,
                        path: 'timeoutAfterUserConverted',
                        defaultValue: '1 day',
                        infotip: t('engageTools.studio.modalWindow.infotips.silenceAfterPurchase'),
                    },
                    {
                        name: 'cta-clicked-timeout',
                        type: 'select',
                        availableOptions: [
                            {
                                name: t('engageTools.studio.config.options.none'),
                                value: 0,
                            },
                            {
                                name: t('engageTools.studio.config.durations.minutes', { minutes: 5 }),
                                value: 300,
                            },
                            {
                                name: t('engageTools.studio.config.durations.minutes', { minutes: 10 }),
                                value: 600,
                            },
                            {
                                name: t('engageTools.studio.config.durations.minutes', { minutes: 15 }),
                                value: 900,
                            },
                            {
                                name: t('engageTools.studio.config.durations.minutes', { minutes: 30 }),
                                value: 1800,
                            },
                            {
                                name: t('engageTools.studio.config.durations.hour'),
                                value: 3600,
                            },
                            {
                                name: t('engageTools.studio.config.durations.hours', { hours: 6 }),
                                value: 21600,
                            },
                            {
                                name: t('engageTools.studio.config.durations.hours', { hours: 12 }),
                                value: 43200,
                            },
                            {
                                name: t('engageTools.studio.config.durations.hours', { hours: 24 }),
                                value: 86400,
                            },
                            {
                                name: t('engageTools.studio.config.durations.hours', { hours: 48 }),
                                value: 172800,
                            },
                        ],
                        label: t('engageTools.studio.config.fields.silenceAfterClick'),
                        span: 8,
                        path: 'timeoutAfterCTAClicked',
                        defaultValue: 1800,
                        infotip: t('engageTools.studio.modalWindow.infotips.silenceAfterClick'),
                    },
                    {
                        name: 'modal-closed-timeout',
                        type: 'select',
                        availableOptions: [
                            {
                                name: t('engageTools.studio.config.options.none'),
                                value: 0,
                            },
                            {
                                name: t('engageTools.studio.config.durations.minutes', { minutes: 5 }),
                                value: 300,
                            },
                            {
                                name: t('engageTools.studio.config.durations.minutes', { minutes: 10 }),
                                value: 600,
                            },
                            {
                                name: t('engageTools.studio.config.durations.minutes', { minutes: 15 }),
                                value: 900,
                            },
                            {
                                name: t('engageTools.studio.config.durations.minutes', { minutes: 30 }),
                                value: 1800,
                            },
                            {
                                name: t('engageTools.studio.config.durations.hour'),
                                value: 3600,
                            },
                            {
                                name: t('engageTools.studio.config.durations.hours', { hours: 6 }),
                                value: 21600,
                            },
                            {
                                name: t('engageTools.studio.config.durations.hours', { hours: 12 }),
                                value: 43200,
                            },
                            {
                                name: t('engageTools.studio.config.durations.hours', { hours: 24 }),
                                value: 86400,
                            },
                            {
                                name: t('engageTools.studio.config.durations.hours', { hours: 48 }),
                                value: 172800,
                            },
                        ],
                        label: t('engageTools.studio.config.fields.silenceAfterClose'),
                        span: 8,
                        path: 'timeoutAfterModalClosed',
                        defaultValue: 1800,
                        infotip: t('engageTools.studio.modalWindow.infotips.silenceAfterClose'),
                    },
                    {
                        name: 'outside-dismiss',
                        type: 'toggle',
                        label: t('engageTools.studio.config.fields.enableOutsideClick'),
                        span: 7,
                        path: 'dismissOnOutsideClick',
                        defaultValue: true,
                        infotip: t('engageTools.studio.modalWindow.infotips.outsideDismiss'),
                    },
                ],
            },
            {
                name: 'Frequency limit',
                label: t('engageTools.studio.config.sections.frequencyLimit'),
                fields: [],
            },
        ],
    },
})
